import React, { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBell } from "@fortawesome/free-solid-svg-icons";
import LanguageSelector from "./LanguageSelector";
import UserProfile from "./UserProfile";
import RedisInfo from "../services/RedisInfo";
import "../styles/SubHeader.css";

const apiUrl = process.env.REACT_APP_API_URL;

const SubHeader = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [status, setStatus] = useState("");
  const [notifications, setNotifications] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [visibleCount, setVisibleCount] = useState(5);
  const [dealerShipId, setDealerShipId] = useState(null);

  const dropdownRef = useRef(null);

  // Get dealerShipId from Redis
  const getDealerShipId = async () => {
    try {
      const redisData = await RedisInfo.redisDetails();
      const dealerShipId = `${redisData.ParentGroup}#${redisData.DealerMapCode}#${redisData.LocCode}`;
      return dealerShipId;
    } catch (error) {
      console.error("Error getting dealerShipId:", error);
      return null;
    }
  };

  // Load seen notifications from localStorage (per dealer)
  const getSeenNotifications = (dealerId) => {
    try {
      if (!dealerId) return [];
      const key = `seenNotifications_${dealerId}`;
      const seen = localStorage.getItem(key);
      return seen ? JSON.parse(seen) : [];
    } catch (error) {
      console.error("Error loading seen notifications:", error);
      return [];
    }
  };

  // Save seen notifications to localStorage (per dealer)
  const saveSeenNotifications = (dealerId, notificationIds) => {
    try {
      if (!dealerId) return;
      const key = `seenNotifications_${dealerId}`;
      localStorage.setItem(key, JSON.stringify(notificationIds));
    } catch (error) {
      console.error("Error saving seen notifications:", error);
    }
  };

  // Close dropdown if clicked outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Format date to show only date (YYYY-MM-DD)
  const formatDateOnly = (dateString) => {
    try {
      const date = new Date(dateString);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    } catch (error) {
      console.error("Error formatting date:", error);
      return dateString;
    }
  };

  // Calculate time difference and format
  const formatTimeAgo = (dateString) => {
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffInSeconds = Math.floor((now - date) / 1000);
      
      if (diffInSeconds < 60) {
        return "Just now";
      } else if (diffInSeconds < 3600) {
        const minutes = Math.floor(diffInSeconds / 60);
        return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
      } else if (diffInSeconds < 86400) {
        const hours = Math.floor(diffInSeconds / 3600);
        return `${hours} hour${hours > 1 ? 's' : ''} ago`;
      } else {
        // Show date only for older notifications
        return formatDateOnly(dateString);
      }
    } catch (error) {
      console.error("Error calculating time ago:", error);
      return formatDateOnly(dateString);
    }
  };

  // Fetch notifications & onboarding status
  const fetchNotifications = async () => {
    try {
      // Get dealerShipId first
      const currentDealerShipId = await getDealerShipId();
      if (!currentDealerShipId) {
        console.warn("Could not get dealerShipId from Redis, skipping notification fetch");
        return;
      }
      
      setDealerShipId(currentDealerShipId);
      console.log("Using dealerShipId from frontend Redis:", currentDealerShipId);
      
      const token = sessionStorage.getItem('token');
      // Pass dealerShipId as query parameter as fallback if backend can't get it from Redis
      const res = await fetch(`${apiUrl}/api/v1/dealerShipDetails/dealer/approvalStatus?dealerShipId=${encodeURIComponent(currentDealerShipId)}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!res.ok) {
        const errorText = await res.text();
        console.error("API error response:", errorText);
        throw new Error(`HTTP error! status: ${res.status}, body: ${errorText}`);
      }
      
      const data = await res.json(); // <- JSON response
      console.log("Notification API response:", JSON.stringify(data, null, 2));
      console.log("DealerShipId from API:", data.dealerShipId);
      console.log("Has notification:", data.hasNotification);
      console.log("Status:", data.status);
      console.log("Approval timestamp:", data.approvalTimestamp);
      
      // If API returned null dealerShipId but we have one from frontend, use frontend one
      const effectiveDealerShipId = data.dealerShipId || currentDealerShipId;
      console.log("Effective dealerShipId (API or frontend):", effectiveDealerShipId);
      
      const seenNotificationIds = getSeenNotifications(effectiveDealerShipId);
      console.log("Seen notification IDs for dealer:", seenNotificationIds);

      // Check if onboarding is approved - check status OR hasNotification flag
      const isApproved = data.status === "ONBOARDING_REQUEST_APPROVED";
      const hasApprovalTimestamp = data.approvalTimestamp != null && data.approvalTimestamp.trim() !== "";
      
      console.log("Is approved:", isApproved, "Has timestamp:", hasApprovalTimestamp);
      
      // If status is NOT_FOUND but we have dealerShipId, try to get dealer details directly
      if (data.status === "NOT_FOUND" && effectiveDealerShipId && !data.dealerShipId) {
        console.log("API returned NOT_FOUND but we have dealerShipId, this might be a backend Redis issue");
        console.log("Backend couldn't get dealerShipId from Redis, but frontend has it:", effectiveDealerShipId);
        // Don't show notification if backend can't find the dealer
        setStatus("");
        setNotifications([]);
        return;
      }
      
      if (isApproved && hasApprovalTimestamp) {
        setStatus("Approved");
        
        // Create unique notification ID using dealerShipId + approval timestamp
        // When user is re-onboarded, they get a NEW timestamp, so this will be a NEW notification ID
        const notificationId = `onboarding_approved_${effectiveDealerShipId}_${data.approvalTimestamp}`;
        console.log("Generated notification ID:", notificationId);
        console.log("Seen notification IDs:", seenNotificationIds);
        console.log("Is notification seen?", seenNotificationIds.includes(notificationId));
        
        // Simple check: if this notification ID hasn't been seen before, show it
        // For re-onboarding: new approval = new timestamp = new notification ID = show notification
        if (!seenNotificationIds.includes(notificationId)) {
          const notificationDate = data.approvalTimestamp;
          console.log("✅ NEW notification detected! Adding notification with timestamp:", notificationDate);
          console.log("This handles re-onboarding case - new approval timestamp = new notification");
          
          setNotifications([
            { 
              id: notificationId, 
              message: "Onboarding request has been approved!", 
              timestamp: notificationDate,
              time: formatTimeAgo(notificationDate),
              date: formatDateOnly(notificationDate),
              type: "success" 
            }
          ]);
        } else {
          console.log("❌ Notification already seen (ID exists in localStorage), not showing");
          setNotifications([]);
        }
      } else {
        console.log("No notification - isApproved:", isApproved, "hasApprovalTimestamp:", hasApprovalTimestamp, "status:", data.status, "timestamp:", data.approvalTimestamp);
        setStatus("");
        setNotifications([]);
      }

    } catch (error) {
      console.error("Error fetching notifications:", error);
      setStatus("");
      setNotifications([]);
    }
  };

  // Update time display periodically
  useEffect(() => {
    if (notifications.length === 0) return;
    
    const updateTimes = () => {
      setNotifications(prev => prev.map(note => ({
        ...note,
        time: formatTimeAgo(note.timestamp),
        date: formatDateOnly(note.timestamp)
      })));
    };

    const interval = setInterval(updateTimes, 60000); // Update every minute
    return () => clearInterval(interval);
  }, [notifications.length]);

  // Clear old notifications when dealerShipId changes (user deleted and re-onboarded)
  useEffect(() => {
    if (dealerShipId) {
      // When dealerShipId is set, check if we need to clear old notifications
      // This handles the case where user was deleted and re-onboarded
      const seenNotifications = getSeenNotifications(dealerShipId);
      console.log("Current dealerShipId:", dealerShipId, "Seen notifications:", seenNotifications);
    }
  }, [dealerShipId]);

  // Polling every 10 seconds
  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 10000); // 10 seconds
    return () => clearInterval(interval);
  }, []);

  const getIcon = (type) => {
    switch (type) {
      case "error": return "❌";
      case "warning": return "⚠️";
      case "success": return "✅";
      default: return "ℹ️";
    }
  };

  const removeNotification = (id) => {
    if (!dealerShipId) return;
    const seenNotificationIds = getSeenNotifications(dealerShipId);
    if (!seenNotificationIds.includes(id)) {
      seenNotificationIds.push(id);
      saveSeenNotifications(dealerShipId, seenNotificationIds);
    }
    setNotifications((prev) => prev.filter((note) => note.id !== id));
  };

  const removeAllNotifications = () => {
    if (!dealerShipId) return;
    const seenNotificationIds = getSeenNotifications(dealerShipId);
    notifications.forEach(note => {
      if (!seenNotificationIds.includes(note.id)) {
        seenNotificationIds.push(note.id);
      }
    });
    saveSeenNotifications(dealerShipId, seenNotificationIds);
    setNotifications([]);
  };

  return (
    <div className="container-fluid p-3 bg-white">
      <div className="d-flex align-items-center justify-content-between">
        {/* Left navigation */}
        <div className="d-flex align-items-center">
          <a
            className="text-dark responsive-text"
            onClick={() => navigate("/home")}
            style={{ cursor: "pointer" }}
          >
            {t("home")}
          </a>
          <span className="mx-2">/</span>
          <a
            className="text-primary responsive-text"
            onClick={() => navigate("/home")}
            style={{ cursor: "pointer" }}
          >
            {t("home_account_details")}
          </a>
        </div>

        {/* Right Section */}
        <div className="d-flex align-items-center gap-3 position-relative">
          <div className="notification-wrapper" ref={dropdownRef}>
            <div style={{ position: "relative", display: "inline-block" }}>
              <FontAwesomeIcon
                icon={faBell}
                size="2x"
                className={`text-dark ${status === "Approved" ? "text-success" : ""}`}
                style={{ cursor: "pointer", padding: "4px" }}
                onClick={() => setShowDropdown(prev => !prev)}
              />
              {notifications.length > 0 && (
                <span className="notification-badge">
                  {notifications.length}
                </span>
              )}
            </div>

            {showDropdown && (
              <div className="notification-dropdown">
                <h6 className="dropdown-title">
                  {notifications.length > 0
                    ? `${notifications.length} New Notification${notifications.length > 1 ? "s" : ""}`
                    : "Notifications"}
                </h6>

                {notifications.length > 0 ? (
                  <>
                    {notifications.slice(0, visibleCount).map((note) => (
                      <div key={note.id} className="dropdown-item d-flex align-items-start justify-content-between">
                        <div className="d-flex">
                          <span className="icon">{getIcon(note.type)}</span>
                          <div className="text ms-2">
                            <div className="message">{note.message}</div>
                            {note.date && <div className="time">{note.date}</div>}
                          </div>
                        </div>
                        <span
                          style={{ cursor: "pointer", color: "red", marginLeft: "10px" }}
                          onClick={() => removeNotification(note.id)}
                        >
                          ×
                        </span>
                      </div>
                    ))}
                    <div
                      className="view-more"
                      style={{ textAlign: "center", marginTop: "10px" }}
                      onClick={removeAllNotifications}
                    >
                      Close All
                    </div>
                  </>
                ) : (
                  <div className="dropdown-item text-muted">No new notifications</div>
                )}
              </div>
            )}
          </div>

          <LanguageSelector />
          <UserProfile />
        </div>
      </div>
    </div>
  );
};

export default SubHeader;
